import { Component, computed, inject, input, linkedSignal, untracked } from '@angular/core';
import { FieldTree, FormField } from '@angular/forms/signals';
import {
  EventTypeEnum,
  TConfirmationModalData,
  TIconMetaData,
  TJunoSelectOption,
} from '@dss-libs/shared/type/common-types';
import { OrdinalPipe } from '@dss-libs/shared/ui/common-pipes';
import { TranslocoDirective } from '@jsverse/transloco';
import { ModalUiService, SnackbarService } from '@dss-libs/shared/utility/common-services';
import {
  ICurrentSubscriptionSignatures,
  IOrderStepFormModel,
  IRecipientFormModel,
  IRecipientInformationFormModel,
  TRecipientTask,
  TRecipientField,
  TStepField,
} from '@dss-libs/portal/type/portal-types';
import {
  ButtonComponent,
  ConfirmationModalComponent,
  DropdownMenuComponent,
  DropdownMenuDirective,
  JunoInput,
  JunoPhoneNumber,
  JunoSelect,
} from '@dss-libs/shared/ui/common-components';
import { MatIcon } from '@angular/material/icon';
import { TranslocoDecimalPipe } from '@jsverse/transloco-locale';
import { createEmptyRecipient, createOrderStep } from '../forms/create-order-form.factory';

@Component({
  selector: 'dss-libs-feature-recipient-information-form',
  imports: [
    TranslocoDirective,
    OrdinalPipe,
    FormField,
    DropdownMenuDirective,
    DropdownMenuComponent,
    JunoInput,
    JunoPhoneNumber,
    JunoSelect,
    ButtonComponent,
    MatIcon,
    TranslocoDecimalPipe,
  ],
  templateUrl: './recipient-information-form.component.html',
  styleUrl: './recipient-information-form.component.css',
})
export class RecipientInformationFormComponent {
  readonly #snackbarService = inject(SnackbarService);
  readonly #modalService = inject(ModalUiService);

  readonly form = input.required<FieldTree<IRecipientInformationFormModel>>();
  readonly subscriptionData = input.required<ICurrentSubscriptionSignatures>();

  readonly addRecipientIcon: TIconMetaData = { icon: 'user-plus-rounded-bold', appliedClasses: 'flex! size-4.5!' };
  protected readonly taskOptions: TJunoSelectOption<TRecipientTask>[] = [
    { value: 'reviewer', label: 'CONTRACT_ORDER.REVIEW_DOCUMENT' },
    { value: 'signer', label: 'CONTRACT_ORDER.SIGN_DOCUMENT' },
  ];

  readonly expandedSteps = linkedSignal<ReadonlySet<TStepField>>(() => {
    const form = this.form();
    return untracked(() => new Set(Array.from(form.steps).slice(0, 1)));
  });

  readonly expandedRecipients = linkedSignal<ReadonlySet<TRecipientField>>(() => {
    const form = this.form();
    return untracked(() => {
      const [firstStep] = Array.from(form.steps);
      return new Set(firstStep ? Array.from(firstStep.recipients).slice(0, 1) : []);
    });
  });

  readonly isReceiveButtonDisabled = computed(() => this.form().steps.length > 0 && this.form()().invalid());

  toggleStepExpansion(step: TStepField): void {
    this.expandedSteps.update((steps) => toggle(steps, step));
  }

  toggleRecipientExpansion(recipient: TRecipientField): void {
    this.expandedRecipients.update((recipients) => toggle(recipients, recipient));
  }

  addOrderStep(): void {
    if (!this.#canAddRecipient()) return;
    const steps = this.form().steps;
    steps().value.update((values) => [...values, createOrderStep()]);
    const addedStep = steps[steps.length - 1];
    this.expandedSteps.update((expanded) => new Set(expanded).add(addedStep));
    this.expandedRecipients.update((expanded) => new Set(expanded).add(addedStep.recipients[0]));
  }

  addRecipientToStep(step: TStepField): void {
    if (!this.#canAddRecipient()) return;
    step.recipients().value.update((recipients) => [...recipients, createEmptyRecipient()]);
    const addedRecipient = step.recipients[step.recipients.length - 1];
    this.expandedRecipients.update((expanded) => new Set(expanded).add(addedRecipient));
  }

  removeRecipientFromStep(recipient: TRecipientField): void {
    const { name, email } = recipient().value();

    if (!name || !email) {
      this.#removeRecipient(recipient);
      return;
    }

    const data: TConfirmationModalData = {
      svgIcon: 'trash-bin-minimalistic-outline',
      iconVariant: 'danger',
      title: 'SIGNING_PARTICIPANT.DELETE_RECIPIENT_TITLE',
      subtitle: 'SIGNING_PARTICIPANT.DELETE_SUBTITLE',
      message: 'SIGNING_PARTICIPANT.DELETE_RECIPIENT_DESCRIPTION',
      primaryButtonLabel: 'SIGNING_PARTICIPANT.REMOVE_RECIPIENT',
      primaryButtonColor: 'danger',
      secondaryButtonLabel: 'GENERAL.CANCEL',
      participant: { name, email },
    };
    this.#modalService.openAbstractModal({
      component: ConfirmationModalComponent,
      dialogData: data,
      onSubmit: () => this.#removeRecipient(recipient),
    });
  }

  removeOrderStep(step: TStepField): void {
    const removedStep = step().value();
    this.form()
      .steps()
      .value.update((steps) => steps.filter((current) => current !== removedStep));
  }

  moveOrderStepUp(step: TStepField): void {
    this.#moveStep(step, -1);
  }

  moveOrderStepDown(step: TStepField): void {
    this.#moveStep(step, 1);
  }

  #removeRecipient(recipient: TRecipientField): void {
    const removedRecipient = recipient().value();
    this.form()
      .steps()
      .value.update((steps) => withoutRecipient(steps, removedRecipient));
  }

  #moveStep(step: TStepField, offset: -1 | 1): void {
    const movedStep = step().value();
    this.form()
      .steps()
      .value.update((steps) => {
        const from = steps.indexOf(movedStep);
        const reordered = [...steps];
        [reordered[from], reordered[from + offset]] = [reordered[from + offset], reordered[from]];
        return reordered;
      });
  }

  #canAddRecipient(): boolean {
    const { pendingSignatures, remainingSignatures } = this.subscriptionData();
    const totalRecipients = this.form()
      .steps()
      .value()
      .reduce((total, step) => total + step.recipients.length, 0);

    if (totalRecipients + pendingSignatures < remainingSignatures || totalRecipients + 1 <= remainingSignatures) {
      return true;
    }

    this.#snackbarService.showToaster(EventTypeEnum.WARNING, 'CONTRACT_ORDER.CANT_ADD_RECIPIENTS', true);
    return false;
  }
}

function toggle<T>(items: ReadonlySet<T>, item: T): ReadonlySet<T> {
  const next = new Set(items);
  if (!next.delete(item)) next.add(item);
  return next;
}

function withoutRecipient(steps: IOrderStepFormModel[], removed: IRecipientFormModel): IOrderStepFormModel[] {
  return steps.flatMap((step) => {
    if (!step.recipients.includes(removed)) return [step];
    const recipients = step.recipients.filter((recipient) => recipient !== removed);
    return recipients.length > 0 ? [{ ...step, recipients }] : [];
  });
}
